import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { linkImagesWithMenuItem } from 'apis/mips/api';
import { PhotoShootItemType } from 'apis/mips/types';
import localize from 'common/utils/localize';

import { getFileNameWithoutExtension, getMenuItemImageIds } from './utils';
import { PhotoShootQueryKeys } from '../../constants/queryKeys';
import { PhotoShootModel } from '../../types';

type UseAutoLinkImagesProps = {
  venueId: string;
  token: string | null;
  items: PhotoShootModel['items'];
  onError?: (error: unknown) => void;
};

type AutoLinkDataSet = {
  image: PhotoShootModel['images'][0];
  item: PhotoShootModel['items'][PhotoShootItemType.MENU_ITEM][0];
};

type HandleLinkImageProps = { dataSet: AutoLinkDataSet[]; index?: number };

export const useAutoLinkImages = ({ venueId, token, items, onError }: UseAutoLinkImagesProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const { mutate: handleRecursiveImagesLinking } = useMutation(
    ({ dataSet, index = 0 }: HandleLinkImageProps) => {
      const { item, image } = dataSet[index];
      const newImageIds = getMenuItemImageIds({
        currentImages: item.images,
        newImageId: image.id,
        newImageSlotIndex: 0,
      });

      return linkImagesWithMenuItem(venueId, item.id, newImageIds, token);
    },
    {
      onSettled: (res, _, { dataSet, index = 0 }) => {
        const isLast = index >= dataSet.length - 1;

        if (res?.data) {
          queryClient.setQueryData(PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token), res.data);
        }

        if (!isLast) {
          handleRecursiveImagesLinking({ dataSet, index: index + 1 });
        }
      },
      onError,
    },
  );

  const getImagesEligibleForAutoLinking = useCallback(
    (images: PhotoShootModel['images'], items: PhotoShootModel['items']) =>
      images.reduce((acc, image) => {
        const fileName = image.name || '';
        const fileNameWithoutExtension = getFileNameWithoutExtension(fileName);
        const item = items[PhotoShootItemType.MENU_ITEM].find(item => {
          const isMatchingItemId = fileNameWithoutExtension.normalize() === item.menuItemId?.normalize();
          const isMatchingItemName =
            fileNameWithoutExtension.toLowerCase().normalize() ===
            localize(item.name, intl.locale).toLowerCase().normalize();

          return isMatchingItemId || isMatchingItemName;
        });

        if (item) {
          return [...acc, { image, item }];
        }

        return acc;
      }, [] as AutoLinkDataSet[]),
    [intl],
  );

  return useCallback(
    (images: PhotoShootModel['images']) => {
      const imagesEligibleForAutoLinking = getImagesEligibleForAutoLinking(images, items);

      if (imagesEligibleForAutoLinking.length > 0) {
        handleRecursiveImagesLinking({ dataSet: imagesEligibleForAutoLinking });
      }
    },
    [items, handleRecursiveImagesLinking, getImagesEligibleForAutoLinking],
  );
};
